/**
 * P&L — плоский список объектов без группировки по периодам.
 * Колонки: клиент / даты (заявка→договор→старт→завершение) / контракт / поступило / расходы / маржа.
 * Фильтры: период закрытия договора (signed_from / signed_to), регион (наследуется).
 * По клику — раскрытие со сводным блоком, line-items расходов и приходами.
 * Кнопка «Подтянуть из AmoCRM» — синкает lead_created_at + contract_signed_at.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { FinanceAuditModal } from "@/components/finance/FinanceAuditModal";
import {
  fetchObjects, fetchObjectCashflow, refreshObjects, updateObject, promoteObjectToProduction, fetchOpexSummary,
  syncObjectsFromProduction,
  formatRub, KIND_COLORS, KIND_LABELS, PAYMENT_LABELS, REVENUE_TYPE_LABELS,
  type ObjectRow, type ObjectStatus, type ObjectCashflow, type CategoryKind, type ExpenseRow,
} from "@/api/finance";
import { ApiError } from "@/api/client";
import { usePeriod } from "@/state/period";


/** Управление шириной колонок с persist в localStorage и drag-handles. */
const PL_COL_KEYS = [
  "expand", "client", "production", "lead", "contract", "manager",
  "started", "completed", "contract_sum", "cash", "expenses", "margin",
] as const;
type PlColKey = typeof PL_COL_KEYS[number];
const PL_COL_DEFAULTS: Record<PlColKey, number> = {
  expand: 24, client: 280, production: 90, lead: 80, contract: 80, manager: 140,
  started: 92, completed: 92, contract_sum: 100, cash: 100, expenses: 100, margin: 100,
};
const PL_STORAGE_KEY = "kb-finance-pl-cols-v1";
const PL_VISIBILITY_KEY = "kb-finance-pl-visible-v1";

const PL_COL_LABELS: Record<PlColKey, string> = {
  expand: "Разворот",
  client: "Клиент",
  production: "Произв-во",
  lead: "Заявка",
  contract: "Договор",
  manager: "Менеджер",
  started: "В работу",
  completed: "Реализовано",
  contract_sum: "Контракт",
  cash: "Поступления",
  expenses: "Расходы",
  margin: "Маржа",
};
void PL_COL_LABELS;

// Колонки, которые нельзя скрыть (всегда видимы)
const PL_COL_REQUIRED: ReadonlySet<PlColKey> = new Set(["expand", "client"]);

function usePlVisibleCols() {
  const [visible, setVisible] = useState<Set<PlColKey>>(() => {
    try {
      const raw = localStorage.getItem(PL_VISIBILITY_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) {
        const set = new Set(arr.filter((k): k is PlColKey => (PL_COL_KEYS as readonly string[]).includes(k)));
        // required всегда добавляем
        for (const k of PL_COL_REQUIRED) set.add(k);
        return set;
      }
    } catch { /* ignore */ }
    return new Set(PL_COL_KEYS);
  });
  useEffect(() => {
    try { localStorage.setItem(PL_VISIBILITY_KEY, JSON.stringify(Array.from(visible))); } catch { /* ignore */ }
  }, [visible]);
  function toggle(k: PlColKey) {
    if (PL_COL_REQUIRED.has(k)) return;
    setVisible(s => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  }
  function reset() { setVisible(new Set(PL_COL_KEYS)); }
  return { visible, toggle, reset };
}

function usePlColumnWidths() {
  const [widths, setWidths] = useState<Record<PlColKey, number>>(() => {
    try {
      const raw = localStorage.getItem(PL_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : {};
      return { ...PL_COL_DEFAULTS, ...saved };
    } catch { return PL_COL_DEFAULTS; }
  });
  useEffect(() => {
    try { localStorage.setItem(PL_STORAGE_KEY, JSON.stringify(widths)); } catch { /* ignore */ }
  }, [widths]);
  function startResize(key: PlColKey, e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key];
    const onMove = (ev: MouseEvent) => {
      ev.preventDefault();
      const w = Math.max(40, startW + ev.clientX - startX);
      setWidths(prev => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }
  function resetWidths() {
    setWidths(PL_COL_DEFAULTS);
  }
  return { widths, startResize, resetWidths };
}

/** Drag-handle на правой границе заголовка для resize колонки. */
void ResizeHandle;
function ResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <span
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
      className="col-resize-handle"
      title="Перетащите чтобы изменить ширину колонки"
    />
  );
}


/**
 * Hover-popover. Рендерится через portal в <body> с position:fixed, чтобы не
 * резался ячейками таблицы с overflow:hidden. Координаты — рядом с триггером.
 */
function HoverTooltip({ trigger, title, lines, color }: {
  trigger: React.ReactNode;
  title?: string;
  lines: string[];
  color: string;
}) {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  function showAt() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: r.right + 10, y: r.top + r.height / 2 });
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showAt}
        onMouseLeave={() => setPos(null)}
        style={{
          display: "inline-flex", alignItems: "center",
          cursor: "help", color, lineHeight: 1,
        }}
      >
        {trigger}
      </span>
      {pos && createPortal(
        <span
          role="tooltip"
          style={{
            position: "fixed", left: pos.x, top: pos.y,
            transform: "translateY(-50%)", zIndex: 9999,
            display: "flex", overflow: "hidden",
            background: "var(--paper)", color: "var(--ink-1)",
            border: "1px solid var(--line)",
            borderRadius: 8, padding: 0,
            boxShadow: "0 6px 24px rgba(20,20,20,0.18), 0 1px 3px rgba(20,20,20,0.08)",
            width: 320, whiteSpace: "normal", textAlign: "left",
            pointerEvents: "none",
          }}
        >
          <span style={{ width: 4, background: color, flexShrink: 0 }}/>
          <span style={{ padding: "10px 14px", display: "block", flex: 1, minWidth: 0 }}>
            {title && (
              <div style={{
                fontWeight: 600, fontSize: 12, color,
                textTransform: "uppercase", letterSpacing: 0.4,
                marginBottom: 8,
              }}>
                {title}
              </div>
            )}
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
              {lines.map((l, i) => (
                <li key={i} style={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--ink-2)", display: "flex", gap: 6 }}>
                  <span style={{ color, flexShrink: 0, fontWeight: 700 }}>·</span>
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </span>
        </span>,
        document.body,
      )}
    </>
  );
}


/** Карточка KPI-группы со списком подметрик (label → value). */
void KpiGroup;
function KpiGroup({ title, lines }: {
  title: string;
  lines: { label: string; value: string | number; color?: string }[];
}) {
  return (
    <div className="card" style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
        {title}
      </div>
      {lines.map((l, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13 }}>
          <span style={{ color: "var(--ink-2)" }}>{l.label}</span>
          <strong style={{ fontVariantNumeric: "tabular-nums", color: l.color || "var(--ink-1)", whiteSpace: "nowrap" }}>{l.value}</strong>
        </div>
      ))}
    </div>
  );
}


/** Подблок раскрытия одного объекта — структура 1:1 с Excel-листом. */
function ObjectExpansion({ objectId }: { objectId: number }) {
  const qc = useQueryClient();
  const q = useQuery<ObjectCashflow>({
    queryKey: ["finance-object-cashflow", objectId],
    queryFn: () => fetchObjectCashflow(objectId),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const updMut = useMutation({
    mutationFn: (payload: { actual_start?: string | null; actual_end?: string | null }) =>
      updateObject(objectId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      void qc.invalidateQueries({ queryKey: ["finance-object-cashflow", objectId] });
    },
  });

  if (q.isLoading) return <div style={{ padding: 14, fontSize: 12, color: "var(--mute)" }}>Загрузка деталей…</div>;
  if (q.error) {
    const msg = q.error instanceof ApiError ? `HTTP ${q.error.status}` : (q.error as Error).message;
    return <div style={{ padding: 14, fontSize: 12, color: "var(--brick)" }}>Ошибка: {msg}</div>;
  }
  const d = q.data;
  if (!d) return null;
  const m = d.margins;
  const obj = d.object;

  const rowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr auto", gap: 16, padding: "4px 12px", fontSize: 13, alignItems: "baseline" };
  const labelStyle: React.CSSProperties = { color: "var(--ink-2)" };
  const valStyle: React.CSSProperties = { fontVariantNumeric: "tabular-nums", fontWeight: 500 };
  const fmt = (v: number) => formatRub(v) + " ₽";
  const fmtPct = (v: number | null, digits = 1) => v === null ? "—" : `${v.toFixed(digits)}%`;
  const fmtSqm = (v: number) => v ? `${v} м²` : "—";

  const kindBadgeColor = (kind: CategoryKind | null) => kind ? KIND_COLORS[kind] : "var(--mute)";
  const kindLabel = (kind: CategoryKind | null) => kind ? KIND_LABELS[kind] : "—";

  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Сводная таблица показателей */}
      <div style={{ background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 6, padding: "8px 0" }}>
        <div style={{ ...rowStyle, fontWeight: 700, fontSize: 14, borderBottom: "1px solid var(--line)", paddingBottom: 8, marginBottom: 4 }}>
          <span>{obj.client_name}{obj.is_promotion && <span className="badge badge-amber" style={{ marginLeft: 8, fontSize: 10 }}>акция</span>}</span>
          <span style={{ ...valStyle, fontSize: 16 }}>{fmt(m.contract_sum)}</span>
        </div>
        <div style={rowStyle}><span style={labelStyle}>Чистыми с беседки</span><span style={{ ...valStyle, color: m.net_with_construction >= 0 ? "var(--green)" : "var(--brick)" }}>{fmt(m.net_with_construction)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Квадратура</span><span style={valStyle}>{fmtSqm(m.sqm)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Расходы на материалы</span><span style={valStyle}>{fmt(m.materials_total)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Аутсорс</span><span style={valStyle}>{fmt(m.outsource_total)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Налоги</span><span style={valStyle}>{fmt(m.taxes_total)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Оклад рабочих (ФОТ)</span><span style={valStyle}>{fmt(m.fot_total)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Оклад рабочих за квадрат</span><span style={valStyle}>{m.fot_per_sqm === null ? "—" : fmt(m.fot_per_sqm)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Стоимость за квадрат</span><span style={valStyle}>{m.price_per_sqm === null ? "—" : fmt(m.price_per_sqm)}</span></div>
        <div style={rowStyle}><span style={labelStyle}>Доля налогов</span><span style={valStyle}>{fmtPct(m.tax_share_pct)}</span></div>
        <div style={{ ...rowStyle, fontWeight: 600, background: "var(--paper-2)", padding: "6px 12px", marginTop: 4 }}>
          <span>Маржа с беседки по производству</span>
          <span style={{ ...valStyle, color: (m.margin_production_pct || 0) >= 20 ? "var(--green)" : (m.margin_production_pct || 0) >= 10 ? "#c97a1f" : "var(--brick)" }}>
            {fmtPct(m.margin_production_pct)}
          </span>
        </div>
        <div style={{ ...rowStyle, fontWeight: 600, borderTop: "1px solid var(--line)", paddingTop: 6, marginTop: 4 }}>
          <span>Итого расход (производственный)</span><span style={valStyle}>{fmt(m.production_costs)}</span>
        </div>
      </div>

      {/* Сроки строительства — редактируемые */}
      <DatesEditor obj={obj} onSave={(payload) => updMut.mutate(payload)} saving={updMut.isPending}/>

      {/* Расходы по статьям */}
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8, color: "var(--ink-2)" }}>
          Расходы по статьям · {d.expenses.length} · {fmt(m.total_expense)}
        </div>
        {d.expenses.length === 0 ? (
          <div className="muted" style={{ fontSize: 12, padding: "8px 4px" }}>Детализации расходов нет</div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl" style={{ fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Дата</th>
                  <th>Статья</th>
                  <th>Описание</th>
                  <th className="num" style={{ width: 130 }}>Сумма</th>
                  <th style={{ width: 110 }}>Категория</th>
                </tr>
              </thead>
              <tbody>
                {d.expenses.map((e: ExpenseRow) => (
                  <tr key={e.id}>
                    <td className="tabular">{e.date}</td>
                    <td><strong>{e.category_name || "—"}</strong></td>
                    <td style={{ color: "var(--ink-3)" }}>
                      {e.description || <span className="muted">—</span>}
                      {e.supplier && <div style={{ fontSize: 11, color: "var(--mute)" }}>{e.supplier}</div>}
                    </td>
                    <td className="num tabular strong">{fmt(e.amount)}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: kindBadgeColor(e.category_kind) }}/>
                        <span style={{ fontSize: 12 }}>{kindLabel(e.category_kind)}</span>
                      </span>
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid var(--line)", fontWeight: 700, background: "var(--paper-2)" }}>
                  <td colSpan={3}>Итого расход</td>
                  <td className="num tabular">{fmt(m.total_expense)}</td>
                  <td/>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Приходы */}
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8, color: "var(--ink-2)" }}>
          Приходы · {d.revenues.length} · {fmt(m.cash_received)}
        </div>
        {d.revenues.length === 0 ? (
          <div className="muted" style={{ fontSize: 12, padding: "8px 4px" }}>Поступлений нет</div>
        ) : (
          <table className="tbl" style={{ fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={{ width: 100 }}>Дата</th>
                <th>Контрагент</th>
                <th style={{ width: 110 }}>Тип</th>
                <th style={{ width: 140 }}>Способ</th>
                <th className="num" style={{ width: 130 }}>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {d.revenues.map(r => (
                <tr key={r.id}>
                  <td className="tabular">{r.received_at}</td>
                  <td>{r.counterparty || <span className="muted">—</span>}</td>
                  <td><span className="badge" style={{ fontSize: 11 }}>{REVENUE_TYPE_LABELS[r.type]}</span></td>
                  <td><span style={{ fontSize: 11 }}>{PAYMENT_LABELS[r.payment_method]}</span></td>
                  <td className="num strong tabular" style={{ color: r.amount < 0 ? "var(--brick)" : "var(--green)" }}>
                    {r.amount < 0 ? "−" : ""}{formatRub(Math.abs(r.amount))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/** Блок «Сроки строительства» — редактирование actual_start / actual_end. */
function DatesEditor({ obj, onSave, saving }: {
  obj: ObjectRow;
  onSave: (payload: { actual_start?: string | null; actual_end?: string | null }) => void;
  saving: boolean;
}) {
  const [start, setStart] = useState<string>(obj.actual_start || "");
  const [end, setEnd] = useState<string>(obj.actual_end || "");
  const dirty = start !== (obj.actual_start || "") || end !== (obj.actual_end || "");

  const fmtAmoDate = (s: string | null) => s ? new Date(s).toLocaleDateString("ru") : "—";

  return (
    <div style={{ background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 6, padding: "10px 14px" }}>
      <div style={{ fontWeight: 600, marginBottom: 8, color: "var(--ink-2)" }}>Сроки</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, fontSize: 12.5 }}>
        <div>
          <div style={{ color: "var(--mute)", fontSize: 11, marginBottom: 2 }}>Заявка пришла (Amo)</div>
          <div style={{ fontVariantNumeric: "tabular-nums" }}>{fmtAmoDate(obj.lead_created_at)}</div>
        </div>
        <div>
          <div style={{ color: "var(--mute)", fontSize: 11, marginBottom: 2 }}>Договор закрыт (Amo)</div>
          <div style={{ fontVariantNumeric: "tabular-nums", color: obj.contract_signed_at ? "var(--green)" : "var(--mute)" }}>{fmtAmoDate(obj.contract_signed_at)}</div>
        </div>
        <div>
          <div style={{ color: "var(--mute)", fontSize: 11, marginBottom: 2 }}>Стройка начата</div>
          <input type="date" className="input-sm" value={start} onChange={e => setStart(e.target.value)} disabled={saving}/>
        </div>
        <div>
          <div style={{ color: "var(--mute)", fontSize: 11, marginBottom: 2 }}>Стройка завершена</div>
          <input type="date" className="input-sm" value={end} onChange={e => setEnd(e.target.value)} disabled={saving}/>
        </div>
      </div>
      {dirty && (
        <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
          <button className="btn btn-sm btn-primary" disabled={saving}
                  onClick={() => onSave({
                    actual_start: start || null,
                    actual_end: end || null,
                  })}>
            {saving ? "Сохранение…" : "Сохранить даты"}
          </button>
          <button className="btn btn-sm btn-ghost" disabled={saving}
                  onClick={() => { setStart(obj.actual_start || ""); setEnd(obj.actual_end || ""); }}>
            Отмена
          </button>
        </div>
      )}
    </div>
  );
}


/** Одна строка плоского списка объектов P&L. */
/** Карточка объекта для tiles/rows представлений (как в ProductionPage).
 *  Tiles: вертикальная плитка ~340px. Rows: компактная строка.
 *  Клик по карточке → раскрывает inline детализацию (ObjectExpansion).
 *  Иконка ↗ — переход на /production/{id}. */
function FinanceObjectCard({ obj, viewMode, expanded, onToggle }: {
  obj: ObjectRow;
  viewMode: "tiles" | "rows";
  expanded: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const promoteMut = useMutation({
    mutationFn: () => promoteObjectToProduction(obj.id),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      void qc.invalidateQueries({ queryKey: ["production"] });
      void navigate({ to: "/production/$projectId" as "/", params: { projectId: String(r.production_project_id) } as never });
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  const isOldUnplanned = !!obj.lead_created_at && obj.lead_created_at < "2025-09-01" && !obj.in_planning;
  const margin = (obj.cash_received || 0) - (obj.total_expense || 0);
  const marginPct = obj.cash_received ? (margin / obj.cash_received) * 100 : null;
  const fmtAmoDate = (s: string | null) => s ? new Date(s).toLocaleDateString("ru", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
  const isDone = obj.production_status === "completed" || !!obj.actual_end || obj.status === "completed";
  const isWorking = !isDone && (obj.production_status === "in_progress" || !!obj.actual_start);
  const statusLabel = isDone ? "Выполнен" : isWorking ? "В работе" : "В ожидании";
  const statusBg = isDone ? "var(--green-soft)" : isWorking ? "var(--brick-50)" : "var(--paper-2)";
  const statusFg = isDone ? "var(--green)" : isWorking ? "var(--brick)" : "var(--mute)";

  const warnings: string[] = [];
  if (!obj.total_expense) warnings.push("Расходы по объекту не зафиксированы");
  if (obj.contract_signed_at && !obj.cash_received) warnings.push("Договор закрыт, но оплат не было");
  if (!obj.deal_amo_id) warnings.push("Объект не связан с AmoCRM");
  if (!obj.production_project_id) warnings.push("Не найден в производственной воронке");
  const alerts: string[] = obj.mismatch_alerts || [];

  const startedCell = obj.production_started_at || obj.actual_start || obj.planned_start;
  const startedFactual = !!(obj.production_started_at || obj.actual_start);
  const endCell = obj.production_completed_at || obj.actual_end || obj.planned_end;
  const endFactual = !!(obj.production_completed_at || obj.actual_end);

  // --- ROWS view: компактная строка ---
  if (viewMode === "rows") {
    return (
      <div
        className={`fin-row${expanded ? " is-expanded" : ""}${isOldUnplanned ? " fin-card-old-unplanned" : ""}`}
        onClick={onToggle}
        style={{
          display: "grid",
          gridTemplateColumns: "1.6fr 110px 110px 110px 110px 110px 120px 40px",
          gap: 12, alignItems: "center",
          padding: "10px 14px",
          background: isOldUnplanned ? "rgba(247,224,138,0.30)" : "var(--card)",
          border: "1px solid var(--line)", borderRadius: 8,
          cursor: "pointer", fontSize: 12.5,
        }}
        title={isOldUnplanned ? "Старая сделка (до сент. 2025) не из планирования" : undefined}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {alerts.length > 0 && <HoverTooltip title="АЛЕРТ" lines={alerts} color="var(--brick)" trigger={<span style={{ fontSize: 13, fontWeight: 700, color: "var(--brick)" }}>!</span>}/>}
            {warnings.length > 0 && <HoverTooltip title="Предупреждения" lines={warnings} color="#c97a1f" trigger={<Icon name="warn" size={12}/>}/>}
            <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{obj.client_name}</strong>
            {obj.is_promotion && <span className="badge badge-amber" style={{ fontSize: 10 }}>акция</span>}
          </div>
          <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
            {obj.region && <span>{obj.region}</span>}
            {obj.sales_manager_name && <span> · {obj.sales_manager_name}</span>}
          </div>
        </div>
        <span style={{ fontSize: 11, color: obj.contract_signed_at ? "var(--green)" : "var(--mute)", whiteSpace: "nowrap" }}>
          {fmtAmoDate(obj.contract_signed_at)}
        </span>
        <span style={{ fontSize: 11, color: startedFactual ? "var(--green)" : startedCell ? "#a85f12" : "var(--mute)", whiteSpace: "nowrap" }}>
          {fmtAmoDate(startedCell || null)}
        </span>
        <span className="num tabular" style={{ whiteSpace: "nowrap" }}>{formatRub(obj.contract_sum, { mln: false })}</span>
        <span className="num tabular" style={{ color: "var(--green)", whiteSpace: "nowrap" }}>{formatRub(obj.cash_received || 0, { mln: false })}</span>
        <span className="num tabular" style={{ color: "var(--brick)", whiteSpace: "nowrap" }}>{formatRub(obj.total_expense || 0, { mln: false })}</span>
        <span className="num tabular strong" style={{ color: margin >= 0 ? "var(--green)" : "var(--brick)", whiteSpace: "nowrap" }}>
          {formatRub(margin, { mln: false })}
          {marginPct !== null && <div style={{ fontSize: 10, fontWeight: 400, color: "var(--mute)" }}>{marginPct.toFixed(1)}%</div>}
        </span>
        <Icon name={expanded ? "chevron_d" : "chevron_r"} size={14}/>
      </div>
    );
  }

  // --- TILES view: плитка ---
  return (
    <div
      className={`fin-card${expanded ? " is-expanded" : ""}${isOldUnplanned ? " fin-card-old-unplanned" : ""}`}
      onClick={onToggle}
      style={{
        background: isOldUnplanned ? "rgba(247,224,138,0.30)" : "var(--card)",
        border: "1px solid var(--line)", borderRadius: "var(--radius-lg, 10px)",
        padding: 14, cursor: "pointer", display: "flex", flexDirection: "column", gap: 10,
        boxShadow: expanded ? "0 4px 16px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.04)",
        height: "100%", minHeight: 220,
        transition: "box-shadow 150ms, border-color 150ms",
      }}
      title={isOldUnplanned ? "Старая сделка (до сент. 2025) не из планирования" : undefined}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = "var(--mute-2)"; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = "var(--line)"; }}
    >
      {/* Header — название + бейджи + ссылки */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {alerts.length > 0 && <HoverTooltip title="АЛЕРТ — данные расходятся" lines={alerts} color="var(--brick)" trigger={<span style={{ fontSize: 14, fontWeight: 700, color: "var(--brick)" }}>!</span>}/>}
            {warnings.length > 0 && <HoverTooltip title="Предупреждения" lines={warnings} color="#c97a1f" trigger={<Icon name="warn" size={12}/>}/>}
            <strong style={{ fontSize: 14, color: "var(--ink-1)" }}>{obj.client_name}</strong>
            {obj.is_promotion && <span className="badge badge-amber" style={{ fontSize: 10 }}>акция</span>}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--mute)", marginTop: 3 }}>
            {obj.region && <span>{obj.region}</span>}
            {obj.sales_manager_name && <span>{obj.region ? " · " : ""}{obj.sales_manager_name}</span>}
            {!obj.sales_manager_name && !obj.region && <span>—</span>}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 12, background: statusBg, color: statusFg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.3 }}>
            {statusLabel}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            {obj.production_project_id ? (
              <Link to={"/production/$projectId" as "/"} params={{ projectId: String(obj.production_project_id) } as never}
                    onClick={e => e.stopPropagation()}
                    title="Открыть карточку в Производстве"
                    style={{ display: "inline-flex", alignItems: "center", color: "var(--brick)", padding: 2 }}>
                <Icon name="external" size={12}/>
              </Link>
            ) : (
              <button type="button"
                      onClick={e => { e.stopPropagation(); if (confirm(`Завести «${obj.client_name}» в Производство?`)) promoteMut.mutate(); }}
                      disabled={promoteMut.isPending}
                      title="В производстве нет — создать карточку"
                      style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: "1px dashed var(--brick)", background: "transparent", color: "var(--brick)", cursor: "pointer", whiteSpace: "nowrap" }}>
                {promoteMut.isPending ? "…" : "+ В произ-во"}
              </button>
            )}
            {obj.amo_url && (
              <a href={obj.amo_url} target="_blank" rel="noopener noreferrer"
                 onClick={e => e.stopPropagation()}
                 title="Сделка в AmoCRM"
                 style={{ display: "inline-flex", alignItems: "center", color: "var(--brick)", padding: 2 }}>
                <Icon name="link" size={12}/>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Даты */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
        <div>
          <div style={{ color: "var(--mute)", textTransform: "uppercase", fontSize: 9.5, letterSpacing: 0.4 }}>Договор закрыт</div>
          <div style={{ color: obj.contract_signed_at ? "var(--green)" : "var(--mute)", fontWeight: 600 }}>{fmtAmoDate(obj.contract_signed_at)}</div>
        </div>
        <div>
          <div style={{ color: "var(--mute)", textTransform: "uppercase", fontSize: 9.5, letterSpacing: 0.4 }}>Реализация</div>
          <div style={{
            color: endFactual ? "var(--green)" : endCell ? "#a85f12" : "var(--mute)",
            fontWeight: 600,
          }}>
            {fmtAmoDate(endCell || null)}
            {!endFactual && endCell && <span style={{ fontSize: 9, marginLeft: 4, color: "#a85f12" }}>(план)</span>}
          </div>
        </div>
      </div>

      {/* Деньги — 3 значения */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, padding: "8px 10px", background: "var(--paper-2)", borderRadius: 6 }}>
        <div>
          <div style={{ fontSize: 9, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Договор</div>
          <div className="tabular" style={{ fontSize: 13, fontWeight: 700 }}>{formatRub(obj.contract_sum, { mln: false })}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Получено</div>
          <div className="tabular" style={{ fontSize: 13, fontWeight: 700, color: "var(--green)" }}>{formatRub(obj.cash_received || 0, { mln: false })}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Расход</div>
          <div className="tabular" style={{ fontSize: 13, fontWeight: 700, color: "var(--brick)" }}>{formatRub(obj.total_expense || 0, { mln: false })}</div>
        </div>
      </div>

      {/* Маржа — прижата к низу карточки чтобы плитки в ряду были одной высоты */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: "auto", paddingTop: 4 }}>
        <span style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Маржа</span>
        <span className="tabular" style={{ fontSize: 16, fontWeight: 700, color: margin >= 0 ? "var(--green)" : "var(--brick)" }}>
          {formatRub(margin, { mln: false })}
        </span>
        {marginPct !== null && (
          <span style={{ fontSize: 11, color: "var(--mute)" }}>· {marginPct.toFixed(1)}%</span>
        )}
        <span style={{ flex: 1 }}/>
        <Icon name={expanded ? "chevron_d" : "chevron_r"} size={14}/>
      </div>
    </div>
  );
}


/** Kanban: 3 столбца по статусу объекта. Drag-and-drop меняет status через updateObject. */
const FIN_KANBAN_DRAG_MIME = "application/x-kb-fin-card";

function getObjectColumn(obj: ObjectRow): "awaiting" | "in_progress" | "completed" {
  const isDone = obj.production_status === "completed" || !!obj.actual_end || obj.status === "completed";
  if (isDone) return "completed";
  const isWorking = obj.production_status === "in_progress" || !!obj.actual_start || obj.status === "in_progress";
  if (isWorking) return "in_progress";
  return "awaiting";
}

function FinanceKanbanCard({ obj, onClick }: { obj: ObjectRow; onClick: () => void }) {
  const fact = obj.cash_received || 0;
  const expect = obj.contract_sum || 0;
  const pct = expect > 0 ? Math.min(100, Math.round((fact / expect) * 100)) : 0;
  const isOldUnplanned = !!obj.lead_created_at && obj.lead_created_at < "2025-09-01" && !obj.in_planning;
  function onDragStart(e: React.DragEvent<HTMLDivElement>) {
    const payload = JSON.stringify({ id: obj.id, fromColumn: getObjectColumn(obj) });
    e.dataTransfer.setData(FIN_KANBAN_DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", payload);
    e.dataTransfer.effectAllowed = "move";
  }
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      style={{
        background: isOldUnplanned ? "rgba(247,224,138,0.30)" : "var(--card)",
        border: "1px solid var(--line)", borderRadius: 8,
        padding: 10, cursor: "grab", display: "flex", flexDirection: "column", gap: 6,
        userSelect: "none",
      }}
      title={isOldUnplanned ? "Старая сделка не из планирования" : undefined}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {obj.client_name}
      </div>
      {(obj.sales_manager_name || obj.region) && (
        <div style={{ fontSize: 11, color: "var(--mute)" }}>
          {[obj.region, obj.sales_manager_name].filter(Boolean).join(" · ")}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span className="tabular" style={{ fontSize: 13, fontWeight: 700 }}>{formatRub(expect, { mln: false })}</span>
        <span style={{ fontSize: 11, color: pct === 100 ? "var(--green)" : pct > 0 ? "var(--brick)" : "var(--mute)" }}>{pct}%</span>
      </div>
      {expect > 0 && (
        <div style={{ height: 3, background: "var(--paper-2)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{
            width: `${pct}%`, height: "100%",
            background: pct === 100 ? "var(--green)" : "var(--brick)",
            transition: "width 0.3s",
          }}/>
        </div>
      )}
    </div>
  );
}

function FinanceKanbanView({ objects, expandedSet, onToggle }: {
  objects: ObjectRow[];
  expandedSet: Set<number>;
  onToggle: (id: number) => void;
}) {
  void expandedSet;
  const qc = useQueryClient();
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const COLUMNS: Array<{ key: "awaiting" | "in_progress" | "completed"; label: string; accent: string; targetStatus: ObjectStatus | null }> = [
    { key: "awaiting",    label: "В ожидании",  accent: "#9e9e9e", targetStatus: "on_hold" },
    { key: "in_progress", label: "В работе",    accent: "#3390ec", targetStatus: "in_progress" },
    { key: "completed",   label: "Реализовано", accent: "#4fae4e", targetStatus: "completed" },
  ];

  const moveMut = useMutation({
    mutationFn: (vars: { id: number; status: ObjectStatus }) =>
      updateObject(vars.id, { status: vars.status }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["finance-objects"] });
      const previous = qc.getQueriesData<ObjectRow[]>({ queryKey: ["finance-objects"] });
      qc.setQueriesData<ObjectRow[] | undefined>({ queryKey: ["finance-objects"] }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map(o => o.id === vars.id ? { ...o, status: vars.status } : o);
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      ctx?.previous?.forEach(([k, v]) => qc.setQueryData(k, v));
      alert("Не удалось перенести сделку — откатываю.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["finance-objects"] }),
  });

  function handleDrop(e: React.DragEvent<HTMLDivElement>, targetStatus: ObjectStatus | null) {
    e.preventDefault();
    setDropTarget(null);
    if (!targetStatus) return;
    let raw = e.dataTransfer.getData(FIN_KANBAN_DRAG_MIME);
    if (!raw) raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as { id: number; fromColumn: string };
      if (!payload.id) return;
      moveMut.mutate({ id: payload.id, status: targetStatus });
    } catch { /* ignore */ }
  }

  const grouped: Record<string, ObjectRow[]> = { awaiting: [], in_progress: [], completed: [] };
  for (const o of objects) grouped[getObjectColumn(o)].push(o);

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(300px, 1fr))",
      gap: 14, alignItems: "start", overflowX: "auto", paddingBottom: 12,
    }}>
      {COLUMNS.map(col => {
        const colItems = grouped[col.key];
        const totalContract = colItems.reduce((s, o) => s + (o.contract_sum || 0), 0);
        const totalCash = colItems.reduce((s, o) => s + (o.cash_received || 0), 0);
        return (
          <div
            key={col.key}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== col.key) setDropTarget(col.key); }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              if (dropTarget === col.key) setDropTarget(null);
            }}
            onDrop={(e) => handleDrop(e, col.targetStatus)}
            style={{
              background: dropTarget === col.key ? "rgba(61,122,61,0.08)" : "var(--paper-2)",
              border: `1px solid ${dropTarget === col.key ? col.accent : "var(--line)"}`,
              borderTop: `3px solid ${col.accent}`,
              borderRadius: 12, padding: 12,
              display: "flex", flexDirection: "column", gap: 8, minHeight: 240,
              transition: "background 100ms, border-color 100ms",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "4px 6px 8px", borderBottom: "1px dotted var(--line)" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>{col.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-1)" }}>{colItems.length}</div>
              </div>
              {totalContract > 0 && (
                <div style={{ fontSize: 11, color: "var(--mute)", textAlign: "right" }}>
                  <div className="tabular">{formatRub(totalContract, { mln: false })}</div>
                  <div className="tabular" style={{ color: "var(--green)" }}>{formatRub(totalCash, { mln: false })}</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {colItems.length === 0 ? (
                <div style={{ color: "var(--mute)", fontSize: 12, textAlign: "center", padding: 20 }}>пусто</div>
              ) : (
                colItems.map(o => <FinanceKanbanCard key={o.id} obj={o} onClick={() => onToggle(o.id)}/>)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


void ObjectRowLine;
function ObjectRowLine({ obj, expanded, onToggle, visible, visibleCount }: {
  obj: ObjectRow;
  expanded: boolean;
  onToggle: () => void;
  visible: Set<PlColKey>;
  visibleCount: number;
}) {
  const isOldUnplannedRow = !!obj.lead_created_at && obj.lead_created_at < "2025-09-01"
    && !obj.in_planning;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const promoteMut = useMutation({
    mutationFn: () => promoteObjectToProduction(obj.id),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      void qc.invalidateQueries({ queryKey: ["production"] });
      void navigate({ to: "/production/$projectId" as "/", params: { projectId: String(r.production_project_id) } as never });
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });
  const margin = (obj.cash_received || 0) - (obj.total_expense || 0);
  const marginPct = obj.cash_received ? (margin / obj.cash_received) * 100 : null;
  const fmtAmoDate = (s: string | null) => s ? new Date(s).toLocaleDateString("ru", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";

  // Сбор предупреждений по объекту (для иконки ⚠ с tooltip)
  const warnings: string[] = [];
  if (!obj.total_expense) warnings.push("Расходы по объекту не зафиксированы");
  if (obj.contract_signed_at && !obj.cash_received) warnings.push("Договор закрыт, но оплат не было");
  if (!obj.sqm) warnings.push("Не указана квадратура (м²)");
  if (!obj.deal_amo_id) warnings.push("Объект не связан с AmoCRM");
  if (!obj.production_project_id) warnings.push("Не найден в производственной воронке");
  // Mismatch-алерты — несоответствия Production ↔ AmoCRM (красный значок)
  const alerts: string[] = obj.mismatch_alerts || [];

  // Логика цветов «Взяли в работу» / «Реализовано»:
  //   factual (Производство есть) → 🟢 зелёный
  //   factual (actual_start/end заполнены вручную) → 🟢 зелёный
  //   planned (planned_start/end из календаря) → 🟡 жёлтый
  //   ничего → серый прочерк
  type Cell = { date: string | null; bg: string; fg: string; tooltip: string };
  const startedCell: Cell = (() => {
    if (obj.production_started_at) return { date: obj.production_started_at, bg: "rgba(61,122,61,0.15)", fg: "var(--green)", tooltip: "Реально взяли в работу (Производство)" };
    if (obj.actual_start) return { date: obj.actual_start, bg: "rgba(61,122,61,0.15)", fg: "var(--green)", tooltip: "Реально взяли в работу (отметил инженер)" };
    if (obj.planned_start) return { date: obj.planned_start, bg: "rgba(232,200,80,0.20)", fg: "#a85f12", tooltip: "Запланировано (производственный календарь)" };
    return { date: null, bg: "transparent", fg: "var(--mute)", tooltip: "" };
  })();
  const endCell: Cell = (() => {
    if (obj.production_completed_at) return { date: obj.production_completed_at, bg: "rgba(61,122,61,0.15)", fg: "var(--green)", tooltip: "Реализовано (Производство)" };
    if (obj.actual_end) return { date: obj.actual_end, bg: "rgba(61,122,61,0.15)", fg: "var(--green)", tooltip: "Реализовано (отметил инженер)" };
    if (obj.planned_end) return { date: obj.planned_end, bg: "rgba(232,200,80,0.20)", fg: "#a85f12", tooltip: "Запланировано (производственный календарь)" };
    return { date: null, bg: "transparent", fg: "var(--mute)", tooltip: "" };
  })();

  return (
    <>
      <tr onClick={onToggle}
          className={isOldUnplannedRow ? "pl-row-old-unplanned" : undefined}
          style={{ cursor: "pointer", background: expanded ? "var(--paper-2)" : undefined, height: 52 }}
          title={isOldUnplannedRow ? "Старая сделка (до сент. 2025), не было в Excel-планировании" : undefined}>
        {visible.has("expand") && (
        <td style={{ paddingRight: 0 }}>
          <Icon name={expanded ? "chevron_d" : "chevron_r"} size={11}/>
        </td>
        )}
        {visible.has("client") && (
        <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
            {alerts.length > 0 && (
              <HoverTooltip
                title="АЛЕРТ — данные расходятся"
                lines={alerts}
                color="var(--brick)"
                trigger={<span style={{ fontSize: 13, fontWeight: 700 }}>!</span>}
              />
            )}
            {warnings.length > 0 && (
              <HoverTooltip
                title="Предупреждения"
                lines={warnings}
                color="#c97a1f"
                trigger={<Icon name="warn" size={12}/>}
              />
            )}
            {obj.production_project_id ? (
              <Link
                to={"/production/$projectId" as "/"}
                params={{ projectId: String(obj.production_project_id) } as never}
                onClick={(e) => e.stopPropagation()}
                title="Открыть карточку в Производстве"
                style={{ color: "var(--ink-1)", textDecoration: "none" }}
              >
                <strong style={{ borderBottom: "1px dashed var(--mute)" }}>{obj.client_name}</strong>
              </Link>
            ) : (
              <strong>{obj.client_name}</strong>
            )}
            {obj.is_promotion && <span className="badge badge-amber" style={{ fontSize: 10 }}>акция</span>}
            {obj.region && <span style={{ fontSize: 11, color: "var(--mute)" }}>· {obj.region}</span>}
            {obj.amo_url && (
              <a href={obj.amo_url} target="_blank" rel="noopener noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 title="Сделка в AmoCRM (продажи)"
                 style={{ display: "inline-flex", alignItems: "center", color: "var(--brick)" }}>
                <Icon name="external" size={11}/>
              </a>
            )}
          </span>
        </td>
        )}
        {/* Производство — ссылка на детальную страницу /v2/production/{id} */}
        {visible.has("production") && (
        <td style={{ textAlign: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
          {obj.production_project_id ? (
            <Link
              to={"/production/$projectId" as "/"}
              params={{ projectId: String(obj.production_project_id) } as never}
              onClick={(e) => e.stopPropagation()}
              title={`Открыть в Производстве (${obj.production_status === "completed" ? "готов" : "в работе"})`}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, color: obj.production_status === "completed" ? "var(--green)" : "var(--brick)", textDecoration: "none" }}
            >
              <Icon name="external" size={12}/>
              <span style={{ fontSize: 10, textDecoration: "underline" }}>{obj.production_status === "completed" ? "готов" : "в работе"}</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (confirm(`Завести «${obj.client_name}» в Производство?\n\nБудет создана карточка с client_name, contract_sum и AmoCRM-ссылкой.`)) promoteMut.mutate(); }}
              disabled={promoteMut.isPending}
              title="В производственной воронке нет карточки. Нажмите чтобы создать."
              style={{
                fontSize: 10, padding: "2px 6px", borderRadius: 4,
                border: "1px dashed var(--brick)", background: "transparent",
                color: "var(--brick)", cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {promoteMut.isPending ? "…" : "+ Завести"}
            </button>
          )}
        </td>
        )}
        {visible.has("lead") && <td style={{ fontSize: 11, color: "var(--mute)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtAmoDate(obj.lead_created_at)}</td>}
        {visible.has("contract") && <td style={{ fontSize: 11, color: obj.contract_signed_at ? "var(--green)" : "var(--mute)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtAmoDate(obj.contract_signed_at)}</td>}
        {visible.has("manager") && <td style={{ fontSize: 11, color: obj.sales_manager_name ? "var(--ink-1)" : "var(--mute)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={obj.sales_manager_name || "Менеджер не назначен в AmoCRM"}>{obj.sales_manager_name || "—"}</td>}
        {visible.has("started") && <td title={startedCell.tooltip} style={{ fontSize: 11, color: startedCell.fg, background: startedCell.bg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtAmoDate(startedCell.date)}</td>}
        {visible.has("completed") && <td title={endCell.tooltip} style={{ fontSize: 11, color: endCell.fg, background: endCell.bg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtAmoDate(endCell.date)}</td>}
        {visible.has("contract_sum") && <td className="num tabular" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatRub(obj.contract_sum)}</td>}
        {visible.has("cash") && <td className="num tabular" style={{ color: "var(--green)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatRub(obj.cash_received || 0)}</td>}
        {visible.has("expenses") && <td className="num tabular" style={{ color: "var(--brick)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatRub(obj.total_expense || 0)}</td>}
        {visible.has("margin") && (
          <td className="num strong tabular" style={{ color: margin >= 0 ? "var(--green)" : "var(--brick)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {formatRub(margin)}
            {marginPct !== null && <div style={{ fontSize: 10, fontWeight: 400, color: "var(--mute)" }}>{marginPct.toFixed(1)}%</div>}
          </td>
        )}
      </tr>
      {expanded && (
        <tr>
          <td colSpan={visibleCount} style={{ padding: 0, background: "var(--paper-2)" }}>
            <ObjectExpansion objectId={obj.id}/>
          </td>
        </tr>
      )}
    </>
  );
}


/** Period picker для P&L — chip-row + кастомный popover, как в ДДС.
 *  Bound to local state (родитель управляет signedFrom/signedTo).
 *  Добавлен пресет «Все» — по умолчанию фильтр не применяется. */
function PnlPeriodPicker({ from, to, onChange }: {
  from: string;
  to: string;
  onChange: (f: string, t: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  useEffect(() => {
    if (open) { setDraftFrom(from); setDraftTo(to); }
  }, [open, from, to]);

  const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
  const isValid = (sv: string) => ISO_RE.test(sv) && !isNaN(new Date(sv + "T00:00:00").getTime());
  const draftValid = isValid(draftFrom) && isValid(draftTo) && draftFrom <= draftTo;
  const draftChanged = draftFrom !== from || draftTo !== to;
  const apply = () => { if (!draftValid) return; onChange(draftFrom, draftTo); setOpen(false); };

  // Расчёт пресетов
  const today = new Date();
  const isoOf = (d: Date) => d.toISOString().slice(0, 10);
  const todayStr = isoOf(today);
  const days = (n: number) => isoOf(new Date(today.getTime() - n * 86400 * 1000));
  const yearStart = isoOf(new Date(Date.UTC(today.getUTCFullYear(), 0, 1)));
  const presets: [string, string, string][] = [
    ["Все",       "",          ""],
    ["Сегодня",   todayStr,    todayStr],
    ["Неделя",    days(6),     todayStr],
    ["Месяц",     days(29),    todayStr],
    ["Квартал",   days(89),    todayStr],
    ["365 дней",  days(364),   todayStr],
    ["Этот год",  yearStart,   todayStr],
  ];
  const isCustom = !!(from || to) && !presets.some(([, f, t]) => f === from && t === to);

  return (
    <div className="chip-row" style={{ position: "relative", display: "inline-flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {presets.map(([label, f, t]) => {
        const active = from === f && to === t;
        return (
          <button key={label}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => { onChange(f, t); setOpen(false); }}>
            {label}
          </button>
        );
      })}
      <button className={`chip ${isCustom ? "active" : ""}`}
              onClick={() => setOpen(o => !o)}
              title="Произвольный период">
        <Icon name="calendar" size={14}/>
        &nbsp;{isCustom ? `${from.slice(5)} — ${to.slice(5)}` : "Период"}
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, marginTop: 8,
          background: "var(--card)", border: "1px solid var(--line)",
          borderRadius: "var(--radius-lg)", padding: 16, zIndex: 50,
          display: "flex", flexDirection: "column", gap: 12, minWidth: 280,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--mute)" }}>
            Кастомный период
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            С
            <input className="input" type="date" value={draftFrom}
                   onChange={(e) => setDraftFrom(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter" && draftValid) apply(); }}/>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            По
            <input className="input" type="date" value={draftTo}
                   onChange={(e) => setDraftTo(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter" && draftValid) apply(); }}/>
          </label>
          {!draftValid && (draftFrom || draftTo) && (
            <div style={{ fontSize: 11, color: "var(--brick)" }}>
              {draftFrom && draftTo && draftFrom > draftTo
                ? "Дата «С» позже даты «По»"
                : "Введите обе даты в формате ГГГГ-ММ-ДД"}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-sm" onClick={() => setOpen(false)}>Отмена</button>
            <button className="btn btn-sm btn-primary" onClick={apply}
                    disabled={!draftValid || !draftChanged}>Применить</button>
          </div>
        </div>
      )}
    </div>
  );
}


type FinanceViewMode = "tiles" | "rows" | "kanban";

const FINANCE_VIEW_KEY = "kb-finance-view-mode";

export function FinancePage() {
  const { region } = usePeriod();
  const qc = useQueryClient();
  const [signedFrom, setSignedFrom] = useState<string>("");
  const [signedTo, setSignedTo] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [auditOpen, setAuditOpen] = useState(false);

  // Tiles / Rows / Kanban — переключатель внизу шапки. Стиль/состав как в ProductionPage.
  const [viewMode, setViewMode] = useState<FinanceViewMode>(() => {
    try {
      const saved = localStorage.getItem(FINANCE_VIEW_KEY);
      if (saved === "tiles" || saved === "rows" || saved === "kanban") return saved;
    } catch { /* ignore */ }
    return "tiles";
  });
  function changeView(v: FinanceViewMode) {
    setViewMode(v);
    try { localStorage.setItem(FINANCE_VIEW_KEY, v); } catch { /* ignore */ }
  }
  // По умолчанию исключаем старые сделки до сент.2025 не из Excel-планирования
  const [includeOldUnplanned, setIncludeOldUnplanned] = useState(false);
  // Фильтр по статусу объекта: all | done (Выполнен) | working (В работе)
  const [statusFilter, setStatusFilter] = useState<"all" | "done" | "working">("all");
  const { widths, startResize, resetWidths } = usePlColumnWidths();
  const { visible: plVisible, toggle: plToggleVisible, reset: plResetVisible } = usePlVisibleCols();
  const [plColsMenuOpen, setPlColsMenuOpen] = useState(false);
  const visiblePlKeys = PL_COL_KEYS.filter(k => plVisible.has(k));
  void widths;
  void startResize;
  void resetWidths;
  void plToggleVisible;
  void plResetVisible;
  void plColsMenuOpen;
  void setPlColsMenuOpen;
  void visiblePlKeys;

  const q = useQuery({
    queryKey: ["finance-objects", region, signedFrom, signedTo],
    queryFn: () => fetchObjects({
      region: region !== "all" ? region : undefined,
      signed_from: signedFrom || undefined,
      signed_to: signedTo || undefined,
    }),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const opexQ = useQuery({
    queryKey: ["finance-opex", region],
    queryFn: () => fetchOpexSummary(region !== "all" ? region : undefined),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const refreshMut = useMutation({
    mutationFn: () => refreshObjects(),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      const p = res.production;
      const s = res.sales;
      const lines = [
        `Производство: ${p.created} новых, ${p.deduped} дублей убрано (всего сделок ${p.total_deals})`,
        `AmoCRM Sales: ${s.matched} сматчено, ${s.unmatched} без пары, ${s.dates_filled} дат заполнено`,
        s.revenue_linked !== undefined ? `Поступления привязаны: ${s.revenue_linked}` : "",
      ].filter(Boolean);
      alert("Обновление сделок\n\n" + lines.join("\n"));
    },
    onError: (e) => alert("Ошибка обновления: " + (e instanceof Error ? e.message : String(e))),
  });
  const syncFromProdMut = useMutation({
    mutationFn: () => syncObjectsFromProduction(),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      alert(`Подтянуто из Производства:\n· создано: ${r.created}\n· обновлено: ${r.updated ?? 0}\n· уже актуально: ${r.skipped}\n· всего сделок в Производстве: ${r.total_production_projects}\n· всего объектов в P&L теперь: ${r.total_objects_now}`);
    },
    onError: (e) => alert("Ошибка: " + (e instanceof Error ? e.message : String(e))),
  });

  function toggle(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const allObjects = q.data || [];
  // «Старая сделка не из планирования» = lead в Amo создан до 2025-09-01 И импорт не из Excel.
  const isOldUnplanned = (o: ObjectRow): boolean =>
    !!o.lead_created_at && o.lead_created_at < "2025-09-01" && !o.in_planning;
  const oldUnplannedCount = allObjects.filter(isOldUnplanned).length;
  const objects = useMemo(() => {
    let list = includeOldUnplanned ? allObjects : allObjects.filter(o => !isOldUnplanned(o));
    if (statusFilter !== "all") {
      list = list.filter(o => {
        const isDone = o.production_status === "completed" || !!o.actual_end || o.status === "completed";
        return statusFilter === "done"
          ? isDone
          : !isDone && (o.production_status === "in_progress" || !!o.actual_start);
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allObjects, includeOldUnplanned, statusFilter]);
  const stats = useMemo(() => {
    let contract = 0, cash = 0, directExpense = 0, importedExpense = 0;
    let productionCosts = 0;
    let countWorking = 0, countWaiting = 0, countDone = 0;
    for (const o of objects) {
      contract += o.contract_sum || 0;
      cash += o.cash_received || 0;
      directExpense += o.total_expense || 0;
      importedExpense += o.imported_total_expense || 0;
      productionCosts += (o.fot_total || 0) + (o.materials_total || 0) + (o.outsource_total || 0) + (o.taxes_total || 0);
      const isDone = o.production_status === "completed" || !!o.actual_end || o.status === "completed";
      const isWorking = !isDone && (o.production_status === "in_progress" || !!o.actual_start);
      if (isDone) countDone++;
      else if (isWorking) countWorking++;
      else countWaiting++;
    }
    const opex = opexQ.data?.total ?? 0;
    const expense = directExpense + opex;
    const expectedRevenue = Math.max(0, contract - cash);
    const futureExpense = Math.max(0, importedExpense - directExpense);
    const profitFact = cash - expense;
    const profitPlan = contract - (importedExpense || expense);
    const marginFactPct = cash > 0 ? (profitFact / cash) * 100 : null;
    const marginProductionPct = contract > 0 ? ((contract - productionCosts) / contract) * 100 : null;
    return {
      total: objects.length, working: countWorking, waiting: countWaiting, done: countDone,
      contract, cash, expectedRevenue,
      directExpense, opex, expense, futureExpense,
      profitFact, profitPlan,
      marginFactPct, marginProductionPct,
    };
  }, [objects, opexQ.data]);

  if (q.isLoading) return <Spinner size="lg" label="Загрузка объектов…" />;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div>;
    }
    const msg = q.error instanceof ApiError ? `HTTP ${q.error.status}` : (q.error as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }

  return (
    <FinanceLayout>
      
      {/* Фильтры — период по дате закрытия договора + статус объекта */}
      <div className="filter-bar" style={{ flexWrap: "wrap", gap: 8, marginBottom: "var(--gap)", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Договор закрыт:</label>
        <PnlPeriodPicker from={signedFrom} to={signedTo}
                         onChange={(f, t) => { setSignedFrom(f); setSignedTo(t); }}/>
        <div style={{ width: 1, height: 20, background: "var(--line)", margin: "0 6px" }}/>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Статус:</label>
        <button className={`chip ${statusFilter === "done" ? "active" : ""}`}
                onClick={() => setStatusFilter(s => s === "done" ? "all" : "done")}
                title="Показать только выполненные объекты">
          Выполнен
        </button>
        <button className={`chip ${statusFilter === "working" ? "active" : ""}`}
                onClick={() => setStatusFilter(s => s === "working" ? "all" : "working")}
                title="Показать только объекты в работе">
          В работе
        </button>
      </div>

      {/* Строка 2: чип «Показать старые сделки» + view-toggle + действия */}
      <div className="filter-bar" style={{ flexWrap: "wrap", gap: 8, marginBottom: "var(--gap)", alignItems: "center" }}>
        {oldUnplannedCount > 0 && (
          <button
            className={`chip ${includeOldUnplanned ? "active" : ""}`}
            onClick={() => setIncludeOldUnplanned(v => !v)}
            title="Сделки в Amo созданы до 1 сентября 2025 и НЕ были в Excel-планировании. По умолчанию исключены из статистики."
            style={includeOldUnplanned ? undefined : { background: "rgba(247,224,138,0.5)", borderColor: "#d4a73a" }}
          >
            {includeOldUnplanned && <Icon name="check" size={11}/>} Показать старые сделки ({oldUnplannedCount})
          </button>
        )}
        <div className="view-toggle">
          <button type="button"
                  className={`view-toggle-btn${viewMode === "tiles" ? " is-active" : ""}`}
                  onClick={() => changeView("tiles")}
                  title="Плитки">
            <Icon name="image" size={14}/>
          </button>
          <button type="button"
                  className={`view-toggle-btn${viewMode === "rows" ? " is-active" : ""}`}
                  onClick={() => changeView("rows")}
                  title="Построчно">
            <Icon name="filter" size={14}/>
          </button>
          <button type="button"
                  className={`view-toggle-btn${viewMode === "kanban" ? " is-active" : ""}`}
                  onClick={() => changeView("kanban")}
                  title="Канбан">
            <Icon name="folder" size={14}/>
          </button>
        </div>
        <button className="btn btn-sm" onClick={() => setAuditOpen(true)} title="Найти и поправить несоответствия данных">
          <Icon name="filter" size={11}/> Проверить и исправить
        </button>
        <button className="btn btn-sm" disabled={syncFromProdMut.isPending}
                onClick={() => syncFromProdMut.mutate()}
                title="Создать объекты в P&L для всех сделок из Производства, которых ещё нет">
          <Icon name="plus" size={11}/> {syncFromProdMut.isPending ? "…" : "Из Производства"}
        </button>
        <button className="btn btn-sm btn-primary" disabled={refreshMut.isPending} onClick={() => refreshMut.mutate()}>
          <Icon name="refresh" size={11}/> {refreshMut.isPending ? "Обновление…" : "Обновить сделки"}
        </button>
      </div>
      <FinanceAuditModal open={auditOpen} onClose={() => setAuditOpen(false)}/>

      {/* === HERO (тёмный): крупная сводка === */}
      <div className="hero hero-overview">
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--mute-2)", marginBottom: 14 }}>
            Финансы · производство и P&L
          </div>
          <div className="hero-headline">
            <span style={{ color: "var(--brick-soft)" }}>{stats.working}</span> в работе,<br/>
            <span style={{ color: "var(--brick-soft)" }}>{stats.waiting}</span> в ожидании
            {stats.done > 0 && <span style={{ fontSize: "0.6em", color: "var(--brick-soft)", verticalAlign: "middle", marginLeft: 8 }}>({stats.done} реализовано)</span>}
          </div>
          <div style={{ marginTop: 18, fontSize: 13.5, color: "var(--mute-2)", maxWidth: 480, lineHeight: 1.5 }}>
            Всего {stats.total} {stats.total % 10 === 1 && stats.total !== 11 ? "объект" : (stats.total % 10 >= 2 && stats.total % 10 <= 4 && (stats.total < 12 || stats.total > 14) ? "объекта" : "объектов")} в производстве. P&L считается из расходов и поступлений по каждой беседке.
          </div>
        </div>

        <div className="hero-stat">
          <div className="hero-stat-label">Выручка по договорам</div>
          <div className="hero-stat-value">
            {(stats.contract / 1_000_000).toFixed(1).replace(".", ",")}<span className="hero-stat-unit">млн ₽</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--mute-2)" }}>
            поступило <strong style={{ color: "var(--paper-2)" }}>{(stats.cash / 1_000_000).toFixed(2).replace(".", ",")} млн</strong>
            {stats.expectedRevenue > 0 && (
              <> · ждём <span style={{ color: "var(--brick-soft)" }}>{(stats.expectedRevenue / 1_000_000).toFixed(1).replace(".", ",")} млн</span></>
            )}
          </div>
        </div>

        <div className="hero-stat">
          <div className="hero-stat-label">Маржа производственная</div>
          <div className="hero-stat-value" style={{ color: (stats.marginProductionPct || 0) >= 20 ? "#79c179" : (stats.marginProductionPct || 0) >= 10 ? "#e8c850" : "var(--brick-soft)" }}>
            {stats.marginProductionPct === null ? "—" : stats.marginProductionPct.toFixed(1).replace(".", ",")}<span className="hero-stat-unit">{stats.marginProductionPct === null ? "" : "%"}</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--mute-2)" }}>
            фактическая {stats.marginFactPct === null ? "—" : `${stats.marginFactPct.toFixed(1).replace(".", ",")}%`}
            {" · "}норма 20%+
          </div>
        </div>
      </div>

      {/* === KPI ряд (4 карточки) === */}
      <div className="row row-4" style={{ marginTop: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Сумма договоров</div>
          <div className="kpi-value">
            {(stats.contract / 1_000_000).toFixed(1).replace(".", ",")}<span className="kpi-unit"> млн ₽</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>{stats.total} объектов</span>
          </div>
        </div>

        <div className={`kpi ${stats.cash === 0 ? "alarm" : ""}`}>
          <div className="kpi-label">Поступило</div>
          <div className="kpi-value">
            {stats.cash >= 1_000_000 ? (stats.cash / 1_000_000).toFixed(1).replace(".", ",") : (stats.cash / 1000).toFixed(0)}
            <span className="kpi-unit"> {stats.cash >= 1_000_000 ? "млн ₽" : "тыс ₽"}</span>
          </div>
          <div className="kpi-foot">
            <span className={`kpi-delta ${stats.cash === 0 ? "down" : ""}`}>
              {stats.cash > 0 ? `${((stats.cash / stats.contract) * 100).toFixed(0)}% от договоров` : "ни одной оплаты"}
            </span>
          </div>
        </div>

        <div className="kpi">
          <div className="kpi-label">Расходов фактических</div>
          <div className="kpi-value" style={{ color: "var(--brick)" }}>
            {(stats.expense / 1_000_000).toFixed(1).replace(".", ",")}<span className="kpi-unit" style={{ color: "var(--brick)" }}> млн ₽</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>
              по объектам: <strong>{(stats.directExpense / 1_000_000).toFixed(1).replace(".", ",")} млн</strong> · прочие: <strong>{(stats.opex / 1_000_000).toFixed(1).replace(".", ",")} млн</strong>
            </span>
          </div>
        </div>

        <div className="kpi feature">
          <div className="kpi-label">Чистая прибыль</div>
          <div className="kpi-value kpi-value-sm" style={{ color: stats.profitFact >= 0 ? "#79c179" : "#e87a7a" }}>
            {stats.profitFact >= 0 ? "" : "−"}
            {Math.abs(stats.profitFact) >= 1_000_000
              ? (Math.abs(stats.profitFact) / 1_000_000).toFixed(1).replace(".", ",")
              : (Math.abs(stats.profitFact) / 1000).toFixed(0)}
            <span className="kpi-unit"> {Math.abs(stats.profitFact) >= 1_000_000 ? "млн ₽" : "тыс ₽"}</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute-2)" }}>
              план: <strong style={{ color: "#fff" }}>{(stats.profitPlan / 1_000_000).toFixed(1).replace(".", ",")} млн ₽</strong>
            </span>
          </div>
        </div>
      </div>



      {/* Список объектов — tiles / rows / kanban */}
      {objects.length === 0 ? (
        <div className="card" style={{ padding: "32px 20px", textAlign: "center" }}>
          <span className="muted">Объектов не найдено</span>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "0 4px" }}>
            <span style={{ fontSize: 12, color: "var(--mute)" }}>
              {objects.length} {objects.length === 1 ? "объект" : "объектов"}
            </span>
            <span style={{ flex: 1 }}/>
            {/* Rows-header: компактные подписи колонок */}
            {viewMode === "rows" && (
              <span style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                клик по строке — детализация
              </span>
            )}
          </div>
          {viewMode === "kanban" ? (
            <FinanceKanbanView objects={objects} expandedSet={expanded} onToggle={toggle}/>
          ) : viewMode === "rows" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Заголовок-шапка rows */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1.6fr 110px 110px 110px 110px 110px 120px 40px",
                gap: 12, padding: "6px 14px",
                fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600,
              }}>
                <span>Клиент</span>
                <span>Договор</span>
                <span>В работу</span>
                <span className="num">Контракт</span>
                <span className="num">Получено</span>
                <span className="num">Расход</span>
                <span className="num">Маржа</span>
                <span/>
              </div>
              {objects.map(o => (
                <div key={o.id}>
                  <FinanceObjectCard obj={o} viewMode="rows" expanded={expanded.has(o.id)} onToggle={() => toggle(o.id)}/>
                  {expanded.has(o.id) && (
                    <div style={{ marginTop: 2, background: "var(--paper-2)", borderRadius: 8, overflow: "hidden" }}>
                      <ObjectExpansion objectId={o.id}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // tiles
            <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", alignItems: "stretch" }}>
              {objects.map(o => (
                <div key={o.id} style={{
                  display: "flex", flexDirection: "column", minWidth: 0,
                  ...(expanded.has(o.id) ? { gridColumn: "1 / -1" } : {}),
                }}>
                  <FinanceObjectCard obj={o} viewMode="tiles" expanded={expanded.has(o.id)} onToggle={() => toggle(o.id)}/>
                  {expanded.has(o.id) && (
                    <div style={{ marginTop: 8, background: "var(--paper-2)", borderRadius: 8, overflow: "hidden" }}>
                      <ObjectExpansion objectId={o.id}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </FinanceLayout>
  );
}
